import { useEffect, useMemo, useRef, useState } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout";
import { Box, IconButton, InputBase, Tooltip } from "@mui/material";
import Icon from "../../../../components/Icon/Icon";
import RenderPiece from "./RenderPiece";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const Grid = WidthProvider(GridLayout);

const COLS = 12;
const ROW_HEIGHT = 30;
const MARGIN = 16;

/**
 * Inline card-title editor. Holds the text LOCALLY and commits to the parent
 * only on blur/Enter, so typing never re-renders the grid (which would make the
 * chart flash on every keystroke). The card title is metadata — it doesn't feed
 * the visualization, so there's no reason to touch global state per letter.
 */
const PieceTitleInput = ({ title, onCommit }) => {
  const [value, setValue] = useState(title || "");
  const focused = useRef(false);

  // Resync if the title changes elsewhere while this input isn't being edited.
  useEffect(() => {
    if (!focused.current) setValue(title || "");
  }, [title]);

  const commit = () => {
    focused.current = false;
    if (value !== (title || "")) onCommit(value);
  };

  return (
    <InputBase
      value={value}
      placeholder="Título…"
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ flex: 1, fontSize: "0.8rem", fontWeight: 600, "& input": { p: 0 } }}
    />
  );
};

/**
 * Lays out a slide's pieces on a 12-col responsive grid. Shared by the viewer
 * (read-only) and the builder (drag/resize + per-piece actions).
 *
 * @param {Array} items          slide pieces (each with { id, layout, ... })
 * @param {boolean} [editable]   enable drag/resize + action overlay
 * @param {(layout) => void} [onLayoutChange]  receives [{id,x,y,w,h}] on change
 * @param {(piece) => void} [onEditPiece]
 * @param {(piece) => void} [onRefreshPiece]
 * @param {(piece) => void} [onRemovePiece]
 * @param {(piece, title) => void} [onRenamePiece]  inline title edit
 * @param {string|null} [refreshingId]  piece currently re-materializing
 * @param {number} [maxRows]  cap the grid height (slides = fixed-height pages)
 */
export const DashboardGrid = ({
  items = [],
  editable = false,
  onLayoutChange,
  onEditPiece,
  onRefreshPiece,
  onRemovePiece,
  onRenamePiece,
  refreshingId,
  maxRows,
}) => {
  const layout = useMemo(
    () =>
      items.map((it) => ({
        i: it.id,
        x: it.layout?.x ?? 0,
        y: it.layout?.y ?? 0,
        w: it.layout?.w ?? 6,
        h: it.layout?.h ?? 8,
        static: !editable,
      })),
    [items, editable]
  );

  const handleLayoutChange = (next) => {
    if (!editable || !onLayoutChange) return;
    onLayoutChange(
      next.map((l) => ({ id: l.i, x: l.x, y: l.y, w: l.w, h: l.h }))
    );
  };

  // Slides are fixed-height pages: cap the grid to maxRows and frame it so the
  // bounds are visible (and match one PDF page when exported later).
  const frameHeight = maxRows
    ? maxRows * ROW_HEIGHT + (maxRows + 1) * MARGIN
    : undefined;

  const grid = (
    <Grid
      className="bi-grid"
      layout={layout}
      cols={COLS}
      rowHeight={ROW_HEIGHT}
      margin={[MARGIN, MARGIN]}
      maxRows={maxRows}
      isDraggable={editable}
      isResizable={editable}
      draggableHandle=".bi-piece__drag"
      onLayoutChange={handleLayoutChange}
      compactType="vertical"
      style={frameHeight ? { minHeight: frameHeight } : undefined}
    >
      {items.map((piece) => (
        <Box
          key={piece.id}
          sx={{
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {editable && (
            <Box
              className="bi-piece__header"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                pl: 1,
                pr: 0.5,
                py: 0.25,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              {/* Inline, editable card title (commits on blur — see component) */}
              <PieceTitleInput
                title={piece.title}
                onCommit={(v) => onRenamePiece?.(piece, v)}
              />
              <Tooltip title="Mover">
                <IconButton
                  size="small"
                  className="bi-piece__drag"
                  sx={{ cursor: "grab" }}
                >
                  <Icon name="bold_more_square" size={14} />
                </IconButton>
              </Tooltip>
              {onRefreshPiece && (
                <Tooltip title="Refrescar">
                  <span>
                    <IconButton
                      size="small"
                      disabled={refreshingId === piece.id}
                      onClick={() => onRefreshPiece(piece)}
                    >
                      <Icon name="bold_refresh_2" size={14} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {onEditPiece && (
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => onEditPiece(piece)}>
                    <Icon name="bold_edit_2" size={14} />
                  </IconButton>
                </Tooltip>
              )}
              {onRemovePiece && (
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => onRemovePiece(piece)}>
                    <Icon name="bold_trash" size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <RenderPiece piece={piece} showTitle={!editable} />
          </Box>
        </Box>
      ))}
    </Grid>
  );

  if (!maxRows) return grid;

  return (
    <Box
      className="bi-slide-frame"
      sx={{
        height: frameHeight,
        overflow: "hidden",
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.015)",
      }}
    >
      {grid}
    </Box>
  );
};

export default DashboardGrid;
